import { CheckCircle2, XCircle } from "lucide-react";
import {
  useDataProvider,
  useGetOne,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";
import { listMissingSaleFields } from "../corretaje/corretajeSale";
import { formatDateOnly } from "../incentives/incentiveProgramForm";
import type { CrmDataProvider } from "../providers/types";
import type {
  AppRole,
  CorretajeSaleAdministration,
  CorretajeVoboDecision,
  CorretajeVoboStatus,
  Sale,
} from "../types";
import { canDecideCorretajeVobo, canEditCorretajeSale } from "./operationRules";

/**
 * Semaforo of the Direccion VoBo: binary, so green (aprobado) or red (anything
 * else), always an icon AND text, never colour alone (design-system.md).
 */
export const VoboStatusBadge = ({
  status,
}: {
  status: CorretajeVoboStatus;
}) => {
  const translate = useTranslate();
  const approved = status === "aprobado";
  const Icon = approved ? CheckCircle2 : XCircle;
  return (
    <span
      data-testid="vobo-status"
      className={cn(
        "inline-flex min-h-7 items-center gap-1.5 rounded-full px-3 text-sm font-medium",
        approved
          ? "bg-[#E7F5EC] text-[#23794A]"
          : "bg-[#FBE7E4] text-[#B3321C]",
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
      {translate(`crm.corretaje.vobo.status.${status}`)}
    </span>
  );
};

const formatStamp = (iso: string) => formatDateOnly(iso.slice(0, 10));

type NoteAction = Extract<CorretajeVoboDecision, "observado" | "reopen">;

/**
 * Almikia Epic EC (TASK-032): the "VoBo de Direccion" block of a corretaje
 * sale. It reads the SAVED administration (the server validates the request
 * again): "Solicitar VoBo" for administration roles once every required field
 * is complete, "Aprobar" / "Observar" / "Reabrir" only for direccion. The
 * Contabilidad step is a later phase and has no action here.
 */
export const CorretajeVoboBlock = ({
  operationId,
  admin,
  role,
  vendedoraHasClave,
  onChanged,
}: {
  operationId: string;
  admin: CorretajeSaleAdministration;
  role: AppRole;
  vendedoraHasClave: boolean;
  onChanged: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteAction, setNoteAction] = useState<NoteAction | null>(null);
  const [note, setNote] = useState("");

  const { data: decider } = useGetOne<Sale>(
    "sales",
    { id: admin.vobo_decided_by ?? "" },
    { enabled: admin.vobo_decided_by != null },
  );

  const status = admin.vobo_status;
  const missing = listMissingSaleFields(admin, vendedoraHasClave);
  const canRequest =
    canEditCorretajeSale(role) &&
    (status === "pendiente" || status === "observado");
  const canDecide = canDecideCorretajeVobo(role);

  const run = async (
    action: () => Promise<void>,
    doneKey: string,
  ): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      notify(doneKey, { type: "success" });
      setNoteAction(null);
      setNote("");
      onChanged();
      refresh();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : translate("crm.operations.error"),
      );
    } finally {
      setBusy(false);
    }
  };

  const decide = (decision: CorretajeVoboDecision, text?: string) =>
    run(
      () => dataProvider.setCorretajeVobo(operationId, decision, text),
      `crm.corretaje.vobo.done.${decision}`,
    );

  return (
    <div className="space-y-3" data-testid="vobo-block">
      <h3 className="font-semibold">{translate("crm.corretaje.vobo.title")}</h3>
      <div className="flex flex-wrap items-center gap-2">
        <VoboStatusBadge status={status} />
        {admin.vobo_decided_at ? (
          <span className="text-sm text-muted-foreground tabular-nums">
            {translate("crm.corretaje.vobo.decided", {
              name: decider
                ? `${decider.first_name} ${decider.last_name}`
                : "—",
              date: formatStamp(admin.vobo_decided_at),
            })}
          </span>
        ) : null}
        {status === "solicitado" && admin.vobo_requested_at ? (
          <span className="text-sm text-muted-foreground tabular-nums">
            {translate("crm.corretaje.vobo.requested", {
              date: formatStamp(admin.vobo_requested_at),
            })}
          </span>
        ) : null}
      </div>
      {admin.vobo_note ? (
        <p className="text-sm">
          <span className="text-muted-foreground">
            {translate("crm.corretaje.vobo.note")}:
          </span>{" "}
          {admin.vobo_note}
        </p>
      ) : null}

      {canRequest ? (
        <p
          data-testid="vobo-readiness"
          className={cn(
            "flex items-center gap-1.5 text-sm font-medium",
            missing.length === 0 ? "text-[#23794A]" : "text-[#B3321C]",
          )}
        >
          {missing.length === 0 ? (
            <CheckCircle2 className="size-4" aria-hidden="true" />
          ) : (
            <XCircle className="size-4" aria-hidden="true" />
          )}
          {missing.length === 0
            ? translate("crm.corretaje.vobo.ready")
            : translate("crm.corretaje.vobo.missing", {
                fields: missing
                  .map((field) =>
                    translate(`crm.corretaje.vobo.missing_fields.${field}`),
                  )
                  .join(", "),
              })}
        </p>
      ) : null}

      {noteAction ? (
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="vobo-note">
            {translate(`crm.corretaje.vobo.note_for.${noteAction}`)}
          </label>
          <Textarea
            id="vobo-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              className="min-h-11"
              disabled={busy || note.trim() === ""}
              onClick={() => decide(noteAction, note.trim())}
            >
              {translate("crm.corretaje.vobo.confirm")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={() => setNoteAction(null)}
            >
              {translate("crm.operations.confirm.back")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {canRequest && missing.length === 0 ? (
            <Button
              type="button"
              variant="gradient"
              className="min-h-11"
              disabled={busy}
              onClick={() =>
                run(
                  () => dataProvider.requestCorretajeVobo(operationId),
                  "crm.corretaje.vobo.done.requested",
                )
              }
            >
              {translate("crm.corretaje.vobo.request")}
            </Button>
          ) : null}
          {canDecide && status === "solicitado" ? (
            <>
              <Button
                type="button"
                variant="gradient"
                className="min-h-11"
                disabled={busy}
                onClick={() => decide("aprobado")}
              >
                {translate("crm.corretaje.vobo.approve")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={busy}
                onClick={() => setNoteAction("observado")}
              >
                {translate("crm.corretaje.vobo.observe")}
              </Button>
            </>
          ) : null}
          {canDecide && status === "aprobado" ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={() => setNoteAction("reopen")}
            >
              {translate("crm.corretaje.vobo.reopen")}
            </Button>
          ) : null}
        </div>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {translate("crm.corretaje.vobo.later_phase_note")}
      </p>
    </div>
  );
};
